import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createHealthCareFacility,
  deleteHealthCareFacility,
  fetchDetailHealthCareFacility,
  fetchHealthCareFacilityDropdownList,
  fetchHealthCareFacilityList,
  updateHealthCareFacility,
} from '@/domains/asset-management/apis/healthcare-management';
import type {
  CreateHealthCareFacilityPayload,
  CreateHealthCareFacilityResponse,
  DeleteHealthCareFacilityResponse,
  HealthCareFacilityDetailResponse,
  HealthCareFacilityDropdownResponse,
  HealthCareFacilityID,
  HealthCareFacilityListResponse,
  UpdateHealthCareFacilityParams,
  UpdateHealthCareFacilityPayload,
  UpdateHealthCareFacilityResponse,
  UseHealthCareManagementProps,
} from '@/domains/asset-management/types/healthcare';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const useHealthCareManagement = ({
  healthCareFacilityId = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  refetchInterval,
}: UseHealthCareManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: healthCareFacilityListData, isLoading: isLoadingHealthCareFacilityList } =
    useQuery<HealthCareFacilityListResponse>({
      queryKey: ['healthCareFacilityListData', queryParams],
      queryFn: () => fetchHealthCareFacilityList(queryParams),
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 1,
      refetchInterval: refetchInterval,
      refetchIntervalInBackground: true,
      initialData: {
        code: '-',
        message: '-',
        data: { total: 0, page: 1, size: 10, total_pages: 0, rows: [] },
      },
      initialDataUpdatedAt: 0,
    });

  const { data: detailHealthCareFacilityData, refetch: refetchDetailHealthCareFacility } = useQuery<
    HealthCareFacilityDetailResponse,
    Error
  >({
    queryKey: ['detailHealthCareFacilityData', healthCareFacilityId],
    queryFn: () => fetchDetailHealthCareFacility(healthCareFacilityId as HealthCareFacilityID),
    retry: false,
    enabled: !!healthCareFacilityId,
  });

  // Dropdown source for Ambulance's health_care_facility_id select. size=0 style
  // full list - unpaginated by design on the backend.
  const { data: healthCareFacilityDropdownData } = useQuery<HealthCareFacilityDropdownResponse>({
    queryKey: ['healthCareFacilityDropdownData'],
    queryFn: () => fetchHealthCareFacilityDropdownList(),
    staleTime: 1000 * 60 * 5,
  });

  const createHealthCareFacilityMutation = useMutation<
    CreateHealthCareFacilityResponse,
    Error,
    CreateHealthCareFacilityPayload
  >({
    mutationFn: (data) => createHealthCareFacility(data),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityListData', queryParams] });
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityDropdownData'] });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteHealthCareFacilityMutation = useMutation<
    DeleteHealthCareFacilityResponse,
    Error,
    HealthCareFacilityID
  >({
    mutationFn: (id) => deleteHealthCareFacility(id),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityListData', queryParams] });
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityDropdownData'] });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateHealthCareFacilityMutation = useMutation<
    UpdateHealthCareFacilityResponse,
    Error,
    {
      healthCareFacilityData: UpdateHealthCareFacilityPayload;
      params: UpdateHealthCareFacilityParams;
    }
  >({
    mutationFn: ({ healthCareFacilityData, params }) =>
      updateHealthCareFacility(healthCareFacilityData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityListData', queryParams] });
          queryClient.invalidateQueries({ queryKey: ['healthCareFacilityDropdownData'] });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    healthCareFacilityListData,
    isLoadingHealthCareFacilityList,
    detailHealthCareFacilityData,
    refetchDetailHealthCareFacility,
    healthCareFacilityDropdownData,
    createHealthCareFacility: createHealthCareFacilityMutation.mutate,
    updateHealthCareFacility: updateHealthCareFacilityMutation.mutate,
    deleteHealthCareFacility: deleteHealthCareFacilityMutation.mutate,
  };
};

export default useHealthCareManagement;
